import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ReplayOutcome } from '@shared/replay';
import { comboMultiplier } from '@shared/scoring';
import { MAX_CUPS, MAX_STARS, type MapProgression } from '@shared/progression';
import { modeLabel, stripVariantSuffix, type BaseChart } from '@shared/chart';
import { variantLabel } from '@shared/gameTypes';
import { TrophyStrip } from '../../components/TrophyStrip';
import { useSeason } from '../../auth/SeasonProvider';
import { useCountUp } from '../../hooks/useCountUp';
import { focusWithoutScroll } from './focusUtils';
import { friendlyDeathReason } from './playMessages';

export interface ResultsOverlayProps {
  outcome: ReplayOutcome;
  submitState: 'idle' | 'submitting' | 'submitted' | 'unranked' | 'skipped' | 'error';
  submitError: string | null;
  onRetry: () => void;
  previousBest: number;
  earnedThisRun: { stars: number; cups: number };
  /**
   * Full chart-lengths the player completed this run. Drives the
   * results title — surviving even one loop reframes the post-death
   * overlay from "Failed" into a celebratory "Run ended" with the
   * loop count, so a player who racked up 12 loops doesn't see
   * flat-red rejection on a meaningful run.
   */
  loopsCleared: number;
  previousProgression: MapProgression;
  competitiveEligible: boolean;
  /**
   * Mode + song + variant of the run that just ended. Rendered as the
   * results card's top "context strip" so screenshots of the post-run
   * dialog stand on their own — a player sharing a Failed result can
   * tell at a glance which mode they were playing, what song, and
   * which difficulty without the page header being in frame.
   */
  chart: Pick<BaseChart, 'mode' | 'title' | 'variant'>;
  /**
   * Where the "Home" link should send the player. Defaults to the
   * mode picker at `/`, which is the right "done with this run" exit
   * for most cases. Pages that want to keep the player inside their
   * current mode can pass `/pulse` or `/circles` instead.
   */
  homeHref?: string;
  retryControl?: ReactNode;
}

/**
 * Post-run modal. Reused across modes — the trophy economy, score
 * presentation, and submit-state banner are identical regardless of
 * gameplay system. Death-reason copy is sourced from the shared
 * {@link friendlyDeathReason} helper so both engines surface the same
 * `DeathReason` vocabulary.
 */
export function ResultsOverlay({
  outcome,
  submitState,
  submitError,
  onRetry,
  previousBest,
  earnedThisRun,
  loopsCleared,
  previousProgression,
  competitiveEligible,
  chart,
  homeHref = '/',
  retryControl,
}: ResultsOverlayProps) {
  const [animatedScore, skipCountUp] = useCountUp(outcome.score, 1100);
  const { refresh: refreshSeason } = useSeason();
  // A saved run can shift a map's podium, which changes who holds crowns —
  // refresh the season standings so the TopNav crown chip stays live.
  useEffect(() => {
    if (submitState === 'submitted') {
      void refreshSeason();
    }
  }, [submitState, refreshSeason]);
  const beatPB = submitState === 'submitted' && previousBest > 0 && outcome.score > previousBest;
  const trophyTotalEarned = earnedThisRun.stars + earnedThisRun.cups;
  const trophyTotalBefore = previousProgression.stars + previousProgression.cups;
  const newBestTrophies = trophyTotalEarned > trophyTotalBefore;
  const showTrophies = trophyTotalEarned > 0 || trophyTotalBefore > 0;
  // Three result framings:
  //   - cleared all played loops (no death):  "Cleared!"
  //   - died after surviving 1+ loops:        "Run ended" with the
  //                                            chart-length count
  //                                            below — celebrates how
  //                                            deep the player got
  //   - died on loop 1 (no full loop cleared): "Failed"
  // `outcome.completed` now means "at least one full loop cleared",
  // so we use `deathReason` to disambiguate clean clears from runs
  // that went down after surviving a loop.
  const died = outcome.deathReason != null;
  const survivedLoops = died && loopsCleared >= 1;
  const titleClass = !died
    ? 'results-title-cleared'
    : survivedLoops
      ? 'results-title-survived'
      : 'results-title-failed';
  const titleText = !died
    ? 'Cleared!'
    : survivedLoops
      ? 'Run ended'
      : 'Failed';
  // Click anywhere on the dim backdrop to skip the count-up animation - rhythm
  // game players tend to mash through results screens.
  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      skipCountUp();
    }
  };
  return (
    <div
      className="play-overlay"
      data-testid="results-overlay"
      onClick={onBackdropClick}
    >
      <div className="play-overlay-card results-card">
        <div className="results-context" data-testid="results-context">
          <span
            className={`results-context-mode results-context-mode-${chart.mode}`}
            data-testid="results-context-mode"
            aria-label={`Mode: ${modeLabel(chart.mode)}`}
          >
            {modeLabel(chart.mode)}
          </span>
          <span
            className="results-context-title"
            data-testid="results-context-title"
          >
            {stripVariantSuffix(chart)}
          </span>
          <span
            className={`variant-badge variant-badge-${chart.variant}`}
            data-testid="results-context-variant"
            aria-label={`Difficulty: ${variantLabel(chart.variant)}`}
          >
            {variantLabel(chart.variant).toUpperCase()}
          </span>
        </div>
        <h3 className={titleClass}>{titleText}</h3>
        {survivedLoops && (
          <p className="results-loops-cleared" data-testid="results-loops-cleared">
            Cleared <strong>{loopsCleared}</strong> full{' '}
            {loopsCleared === 1 ? 'loop' : 'loops'} before going down.
          </p>
        )}
        {beatPB && (
          <div className="pb-badge" data-testid="pb-badge">
            New personal best!
          </div>
        )}
        {showTrophies && (
          <div className="results-trophies" data-testid="results-trophies">
            <TrophyStrip
              stars={earnedThisRun.stars}
              cups={earnedThisRun.cups}
              showWhenEmpty
              size="md"
            />
            <span className="results-trophies-caption">
              {submitState === 'unranked'
                ? 'Practice trophies — not saved'
                : newBestTrophies
                ? `New best! (was ${trophyTotalBefore === 0 ? 'no trophies' : `${previousProgression.stars}★ ${previousProgression.cups}🏆`})`
                : earnedThisRun.stars === MAX_STARS && earnedThisRun.cups === MAX_CUPS
                  ? 'Maxed out!'
                  : `Best: ${previousProgression.stars}★ ${previousProgression.cups}🏆`}
            </span>
          </div>
        )}
        {submitState === 'submitted' && !competitiveEligible && (
          <p className="muted">Saved to this map only · no season rewards</p>
        )}
        <div className="result-stats">
          <div>
            <strong>Score</strong>
            <span data-testid="result-score">{animatedScore.toLocaleString()}</span>
          </div>
          <div>
            <strong>Accuracy</strong>
            <span>{(outcome.accuracy * 100).toFixed(1)}%</span>
          </div>
          <div>
            <strong>Max combo</strong>
            <span>{outcome.maxCombo.toLocaleString()}</span>
          </div>
          <div>
            <strong>Max multiplier</strong>
            <span>x{comboMultiplier(outcome.maxCombo)}</span>
          </div>
          <div>
            <strong>Perfect</strong>
            <span>{outcome.perfects.toLocaleString()}</span>
          </div>
          <div>
            <strong>Good</strong>
            <span>{outcome.goods.toLocaleString()}</span>
          </div>
          {(outcome.oks ?? 0) > 0 && (
            <div>
              <strong>OK</strong>
              <span>{(outcome.oks ?? 0).toLocaleString()}</span>
            </div>
          )}
          <div>
            <strong>Miss</strong>
            <span>{outcome.misses.toLocaleString()}</span>
          </div>
        </div>
        {died && (
          <p className="muted">
            {friendlyDeathReason(outcome.deathReason!)}
            {outcome.deathBeat != null && (
              <> (beat {outcome.deathBeat.toFixed(1)})</>
            )}
          </p>
        )}
        {submitState === 'submitting' && <p className="muted">Submitting score...</p>}
        {submitState === 'submitted' && <p className="ok-pill">Score saved</p>}
        {submitState === 'unranked' && (
          <p className="muted" role="status" data-testid="practice-result">
            Saved as practice — we couldn't verify this run for the leaderboard or trophies.
            {' '}Check your connection, then retry for a ranked run.
          </p>
        )}
        {submitState === 'skipped' && (
          <p className="muted faint">Run ended before a score could be saved. Try again!</p>
        )}
        {submitState === 'error' && (
          <p className="err-pill" data-testid="submit-error">
            {submitError}
          </p>
        )}
        {retryControl}
        <div className="result-actions">
          <button type="button" className="btn btn-primary" ref={focusWithoutScroll} onClick={onRetry}>
            Retry <span className="kbd-hint">(R)</span>
          </button>
          <Link to={homeHref} className="btn">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
